import { Amplify, Auth, Hub } from 'aws-amplify';
import ROUTES_PATH from '@/router/routesPathConfig';
import { generatePath } from 'react-router-dom';
import {
  checkForTokenInactivityTimeout,
  InactivityTimeoutError,
  persistTokenRefreshTime,
} from '@/initializers/awsAmplify/helpers';
import { partial } from 'lodash/fp';
import { notify } from '@/lib/errorLogger';
import { cognitoUsernameSelector, signOutAction } from '@/domain/env';
import { setRuntimeConfig } from '@/lib/runtimeConfig';
import { requestCognitoConfig } from '@/initializers/envConfigRequest';
import * as Sentry from '@sentry/browser';

const DEFAULT_ENV = process.env.REACT_APP_ENV === 'local' ? process.env : {};

const ENV_PREFIX = 'REACT_APP_';

async function awsAmplifyInitializer() {
  try {
    // Retries transient failures internally (2 retries, 500ms then 1s) and checks the
    // response status before we parse it; see @/initializers/envConfigRequest. Failure
    // reporting stays in the catch below, which sits outside the retry.
    const response = await requestCognitoConfig();
    const configuration = await response.json();

    const env = Object.entries(configuration).reduce((acc, [k, v]) => {
      const key = `${ENV_PREFIX}${k}`;
      acc[key] = DEFAULT_ENV[key] || v;

      return acc;
    }, {});

    const {
      REACT_APP_COGNITO_REGION,
      REACT_APP_COGNITO_USER_POOL_ID,
      REACT_APP_COGNITO_USER_POOL_WEB_CLIENT_ID,
      REACT_APP_COGNITO_COOKIE_DOMAIN,
      REACT_APP_COGNITO_LOGIN_URL,
      REACT_APP_COGNITO_HOSTED_UI_DOMAIN,
      REACT_APP_COGNITO_RESPONSE_TYPE,
      REACT_APP_COGNITO_INACTIVITY_TIMEOUT,
    } = env;

    setRuntimeConfig('cognito', {
      REACT_APP_COGNITO_LOGIN_URL,
      REACT_APP_COGNITO_COOKIE_DOMAIN,
      REACT_APP_COGNITO_INACTIVITY_TIMEOUT,
    });

    Amplify.configure({
      Auth: {
        region: REACT_APP_COGNITO_REGION,
        userPoolId: REACT_APP_COGNITO_USER_POOL_ID,
        userPoolWebClientId: REACT_APP_COGNITO_USER_POOL_WEB_CLIENT_ID,
        mandatorySignIn: false,
        cookieStorage: {
          domain: REACT_APP_COGNITO_COOKIE_DOMAIN,
          path: '/',
          expires: 365,
          secure: true,
        },
      },
    });

    // in case of IDp initiated login app starts at /idp route
    // redirectSignIn config value must be set to domain/idp in this only case
    const IDpRoute = generatePath(ROUTES_PATH.AUTH_IDP_LOGIN.absolute);
    const redirectSignIn =
      window.location.pathname === IDpRoute ? window.location.origin + IDpRoute : REACT_APP_COGNITO_LOGIN_URL;

    Auth.configure({
      oauth: {
        domain: REACT_APP_COGNITO_HOSTED_UI_DOMAIN,
        scope: ['phone', 'email', 'openid'],
        redirectSignIn,
        redirectSignOut: REACT_APP_COGNITO_LOGIN_URL,
        responseType: REACT_APP_COGNITO_RESPONSE_TYPE,
      },
    });

    // sign-in event happens on login app as of migration to unified login so
    // this invocation migrates here
    persistTokenRefreshTime(new Date().getTime());
  } catch (e) {
    Sentry.captureMessage('Server cognito config load failed', {
      level: 'warning',
      tags: { source: 'SERVER_CONFIG' },
      extra: {
        cause: e,
      },
    });
    throw new Error('Failed to get auth config', { cause: e });
  }
}

const logger = partial(notify.captureEvent, ['Cognito Auth Event']);

const eventHandler = () => async (response) => {
  const {
    payload: { event, data },
  } = response;

  switch (event) {
    case 'signOut':
      break;
    case 'tokenRefresh':
      persistTokenRefreshTime(new Date().getTime());
      break;
    case 'signIn_failure':
    case 'cognitoHostedUI_failure':
    case 'customState_failure':
      logger({ data: { message: data.message, event } }, 'Cognito login failure');
      break;
    default:
      break;
  }
};

export const initCognitoEventHub = ({ dispatch, getState }) => {
  Hub.listen('auth', eventHandler());
  // this handles case when user is authorized in another tab with other user id or
  // logged auth without further authorization
  document.addEventListener('visibilitychange', async () => {
    const isVisible = document.visibilityState === 'visible';
    if (isVisible) {
      try {
        checkForTokenInactivityTimeout();
        const { username } = await Auth.currentAuthenticatedUser();
        const state = getState();
        const cognitoUsername = cognitoUsernameSelector(state);
        // cognitoUsername can be null sporadicaly if user opens new tab and 'visibilitychange'
        // event handler runs before we retrieve userProfile
        // in this case we dont want to check for username correspondence
        if (cognitoUsername !== null && username?.toLowerCase() !== cognitoUsername?.toLowerCase()) {
          console.warn('Redirected to companies list view as user is likely to have changed identity in another tab');
          window.location = '/';
        }
      } catch (error) {
        dispatch({ type: signOutAction.type });
        if (error instanceof InactivityTimeoutError) {
          console.error('Inactivity timeout reached. User is about to be logged out.', error);
        } else {
          console.error(
            "This message comes from user being logged out in another tab/device when we try to get user session after initial tab is activated. It doesn't indicate an issue.",
            error,
          );
        }
      }
    }
  });
};

export default awsAmplifyInitializer;
